import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useExamSession } from "@/contexts/ExamSessionContext";
import { hasCohortAccessFromEnrollment } from "@/lib/institutional-enrollment";
import { onAppResume, runWhenIdle } from "@/lib/app-resume";
import { getActiveEnrollmentCached, activeEnrollmentQueryKey } from "@/hooks/useActiveEnrollment";
import {
  deriveSubscriptionState,
  resolveBillingManagementType,
  type BillingManagementType,
  type SubscriptionProvider as SubProviderId,
} from "@/lib/iap/subscription-provider";

interface SubscriptionState {
  hasActiveSubscription: boolean;
  /** True when access comes from an active institutional cohort */
  hasCohortAccess: boolean;
  /** True when user appears in blocked_users table */
  isBlocked: boolean;
  /** True when user has has_free_access=true in profiles */
  hasFreeAccess: boolean;
  /** True when institutional user hasn't set their password yet */
  needsPasswordSetup: boolean;
  /** True when the user is currently within an active 7-day trial window */
  isOnTrial: boolean;
  /** ISO timestamp of trial expiration, or null when not on trial */
  trialEndsAt: string | null;
  isInitiallyLoaded: boolean;
  lastCheckedAt: number | null;
  /** Preferred active paid provider — or null when no paid sub is active. */
  subscriptionProvider: SubProviderId;
  hasActiveStripeSubscription: boolean;
  hasActiveAppleSubscription: boolean;
  hasActiveGooglePlaySubscription: boolean;
  hasMultiplePaidSubscriptions: boolean;
  /** Product id of the active paid subscription, when known. */
  subscriptionProductId: string | null;
  /** Renewal / expiration date of the active paid subscription, when known. */
  subscriptionEndsAt: string | null;
  /** Which billing surface (if any) Settings should expose. */
  billingManagementType: BillingManagementType;
}

interface SubscriptionContextType extends SubscriptionState {
  revalidate: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SUBSCRIPTION_STORAGE_KEY = "mblearn_subscription_state";
/** Cache version — bump when access logic changes to auto-invalidate old caches */
export const SUBSCRIPTION_CACHE_VERSION = 4;
/** Max age before cache is treated as stale and ignored on init */
export const SUBSCRIPTION_CACHE_MAX_AGE_MS = 3 * 60 * 1000; // 3 minutes (was 24h)
/** Background revalidation interval */
const REVALIDATE_INTERVAL = 2 * 60 * 1000; // 2 minutes (was 5)
const BLOCKED_CACHE_TTL = 2 * 60 * 1000; // 2 minutes (was 5)

/** Trial duration kept aligned with DB (is_on_trial / get_trial_recipients_for_cron) */
const TRIAL_DURATION_MS = 7 * 24 * 60 * 60 * 1000;

const EMPTY_IAP_EXTRAS = {
  subscriptionProvider: null as SubProviderId,
  hasActiveStripeSubscription: false,
  hasActiveAppleSubscription: false,
  hasActiveGooglePlaySubscription: false,
  hasMultiplePaidSubscriptions: false,
  subscriptionProductId: null as string | null,
  subscriptionEndsAt: null as string | null,
  billingManagementType: null as BillingManagementType,
};

const DEFAULT_STATE: SubscriptionState = {
  hasActiveSubscription: false,
  hasCohortAccess: false,
  isBlocked: false,
  hasFreeAccess: false,
  needsPasswordSetup: false,
  isOnTrial: false,
  trialEndsAt: null,
  isInitiallyLoaded: false,
  lastCheckedAt: null,
  ...EMPTY_IAP_EXTRAS,
};

export function getSubscriptionCacheDiagnostics(userId?: string) {
  if (!userId) {
    return {
      exists: false,
      valid: false,
      stale: false,
      corrupted: false,
      userMismatch: false,
      versionMismatch: false,
      lastCheckedAt: null as number | null,
      ageMs: null as number | null,
    };
  }

  const raw = localStorage.getItem(SUBSCRIPTION_STORAGE_KEY);
  if (!raw) {
    return {
      exists: false,
      valid: false,
      stale: false,
      corrupted: false,
      userMismatch: false,
      versionMismatch: false,
      lastCheckedAt: null as number | null,
      ageMs: null as number | null,
    };
  }

  try {
    const parsed = JSON.parse(raw);
    const lastCheckedAt = typeof parsed.lastCheckedAt === 'number' ? parsed.lastCheckedAt : null;
    const ageMs = lastCheckedAt ? Date.now() - lastCheckedAt : null;
    const versionMismatch = parsed.cacheVersion !== SUBSCRIPTION_CACHE_VERSION;
    const userMismatch = parsed.userId !== userId;
    const stale = ageMs !== null ? ageMs > SUBSCRIPTION_CACHE_MAX_AGE_MS : true;
    const valid = !versionMismatch && !userMismatch && !stale;

    return {
      exists: true,
      valid,
      stale,
      corrupted: false,
      userMismatch,
      versionMismatch,
      lastCheckedAt,
      ageMs,
    };
  } catch {
    return {
      exists: true,
      valid: false,
      stale: false,
      corrupted: true,
      userMismatch: false,
      versionMismatch: false,
      lastCheckedAt: null as number | null,
      ageMs: null as number | null,
    };
  }
}

/** Safely read cached state — returns DEFAULT if version mismatch, user mismatch, or stale */
function readCache(userId: string | undefined): SubscriptionState {
  if (!userId) return { ...DEFAULT_STATE };
  try {
    const stored = localStorage.getItem(SUBSCRIPTION_STORAGE_KEY);
    if (!stored) return { ...DEFAULT_STATE };
    const parsed = JSON.parse(stored);
    // Version gate
    if (parsed.cacheVersion !== SUBSCRIPTION_CACHE_VERSION) {
      console.log('🗑️ [SubCache] Version mismatch — ignoring stale cache');
      localStorage.removeItem(SUBSCRIPTION_STORAGE_KEY);
      return { ...DEFAULT_STATE };
    }
    // User gate
    if (parsed.userId !== userId) {
      localStorage.removeItem(SUBSCRIPTION_STORAGE_KEY);
      return { ...DEFAULT_STATE };
    }
    // TTL gate
    if (Date.now() - parsed.lastCheckedAt > SUBSCRIPTION_CACHE_MAX_AGE_MS) {
      console.log('🗑️ [SubCache] Cache expired (TTL) — ignoring');
      return { ...DEFAULT_STATE };
    }
    return {
      hasActiveSubscription: parsed.hasActiveSubscription,
      hasCohortAccess: parsed.hasCohortAccess ?? false,
      isBlocked: false, // always re-verify on mount
      hasFreeAccess: false, // always re-verify on mount
      needsPasswordSetup: false, // always re-verify on mount
      isOnTrial: parsed.isOnTrial ?? false,
      trialEndsAt: parsed.trialEndsAt ?? null,
      isInitiallyLoaded: false, // always re-verify on mount
      lastCheckedAt: parsed.lastCheckedAt,
      // Provider-specific fields are ALWAYS re-derived from a live fetch;
      // cache never determines PRO entitlement or billing surface.
      ...EMPTY_IAP_EXTRAS,
    };
  } catch {
    localStorage.removeItem(SUBSCRIPTION_STORAGE_KEY);
    return { ...DEFAULT_STATE };
  }
}

function writeCache(state: SubscriptionState, userId: string) {
  localStorage.setItem(SUBSCRIPTION_STORAGE_KEY, JSON.stringify({
    ...state,
    userId,
    cacheVersion: SUBSCRIPTION_CACHE_VERSION,
  }));
}

export function clearSubscriptionCache() {
  localStorage.removeItem(SUBSCRIPTION_STORAGE_KEY);
  console.log('🗑️ [SubCache] Cleared');
}

export const SubscriptionProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [state, setState] = useState<SubscriptionState>(() => readCache(user?.id));
  const blockedCacheRef = useRef<{ value: boolean; ts: number } | null>(null);
  /** Guards the Stripe self-heal so it runs at most once per session per user. */
  const selfHealAttemptedRef = useRef<string | null>(null);
  const prevUserIdRef = useRef<string | undefined>(user?.id);

  // --- Clear cache on user change (login/logout/switch) ---
  useEffect(() => {
    if (prevUserIdRef.current !== user?.id) {
      console.log('👤 [SubCache] User changed — clearing cache');
      clearSubscriptionCache();
      blockedCacheRef.current = null;
      setState({ ...DEFAULT_STATE });
      prevUserIdRef.current = user?.id;
    }
  }, [user?.id]);

  const checkSubscription = useCallback(async () => {
    if (!user) {
      setState({ ...DEFAULT_STATE, isInitiallyLoaded: true, lastCheckedAt: Date.now() });
      clearSubscriptionCache();
      return;
    }

    try {
      console.log('🔍 [SubscriptionContext] Checking subscription for user:', user.id);

      // --- Blocked check (cached with short TTL) ---
      let isBlocked = false;
      const now = Date.now();
      if (blockedCacheRef.current && (now - blockedCacheRef.current.ts) < BLOCKED_CACHE_TTL) {
        isBlocked = blockedCacheRef.current.value;
      } else {
        const { data: blockedData, error: blockedError } = await supabase
          .from('blocked_users')
          .select('user_id')
          .eq('user_id', user.id)
          .maybeSingle();
        if (blockedError && blockedError.code !== 'PGRST116') {
          console.error('❌ Error checking blocked status:', blockedError);
        }
        isBlocked = !!blockedData;
        blockedCacheRef.current = { value: isBlocked, ts: now };
      }

      if (isBlocked) {
        console.log('❌ User is BLOCKED - denying access');
        const s: SubscriptionState = { ...DEFAULT_STATE, isBlocked: true, isInitiallyLoaded: true, lastCheckedAt: Date.now() };
        setState(s);
        writeCache(s, user.id);
        return;
      }

      // --- Parallel: free access + subscription + cohort (shared cache) ---
      const [profileResult, subResult, activeEnrollment] = await Promise.all([
        supabase.from('profiles').select('has_free_access, needs_password_setup, password_set_at, trial_started_at, trial_consumed_at').eq('user_id', user.id).single(),
        supabase.from('subscriptions').select('status, current_period_end, purchase_platform, product_id').eq('user_id', user.id).in('status', ['active', 'trialing']),
        getActiveEnrollmentCached(queryClient, user.id),
      ]);

      // Password setup status — DEFENSIVE.
      // Treat user as "needs password setup" ONLY when:
      //   (a) the flag is explicitly true, AND
      //   (b) password_set_at IS NULL (they have NEVER set a password).
      // This protects against a stale/inconsistent flag forcing an already-activated
      // user back into /auth/set-password after a normal login.
      const flagRaw = profileResult.data?.needs_password_setup === true;
      const passwordSetAt = profileResult.data?.password_set_at ?? null;
      const needsPasswordSetup = flagRaw && !passwordSetAt;
      console.log('🔑 [SubscriptionContext] password setup state:', {
        userId: user.id,
        flagRaw,
        passwordSetAt,
        effectiveNeedsPasswordSetup: needsPasswordSetup,
      });

      // Self-heal REMOVED (2026-08): needs_password_setup is a backend-only
      // column now (UPDATE revoked for `authenticated`, trigger denies changes).
      // The defensive read above already neutralises a stale flag client-side:
      // a row with password_set_at NOT NULL never routes to /auth/set-password.
      // Legacy rows are healed server-side by send-student-access.
      if (flagRaw && passwordSetAt) {
        console.warn('🩹 [SubscriptionContext] Stale needs_password_setup flag ignored (read-only heal)');
      }

      // --- 7-day trial computation ---
      // Active trial = trial_started_at present, NOT consumed, and within 7-day window.
      // Mirrors public.is_on_trial() so client and server stay consistent.
      let trialStartedAt = profileResult.data?.trial_started_at ?? null;
      let trialConsumedAt = profileResult.data?.trial_consumed_at ?? null;

      // Auto-activate trial for eligible legacy users (never started, never
      // consumed, no subscription). New signups already get trial_started_at
      // set by handle_new_user; this covers profiles created before that.
      const subRows = Array.isArray(subResult.data) ? subResult.data : [];
      const hasAnySub = subRows.length > 0;
      if (!trialStartedAt && !trialConsumedAt && !hasAnySub && profileResult.data?.has_free_access !== true) {
        try {
          const { data: ensured, error: ensureErr } = await supabase
            .rpc('ensure_trial_started', { _user_id: user.id });
          if (ensureErr) {
            console.warn('⚠️ [SubscriptionContext] ensure_trial_started failed:', ensureErr.message);
          } else if (ensured && ensured.length > 0) {
            trialStartedAt = ensured[0].trial_started_at ?? trialStartedAt;
            trialConsumedAt = ensured[0].trial_consumed_at ?? trialConsumedAt;
            console.log('🎁 [SubscriptionContext] Auto-started trial:', { trialStartedAt });
          }
        } catch (e) {
          console.warn('⚠️ [SubscriptionContext] ensure_trial_started threw:', e);
        }
      }

      let isOnTrial = false;
      let trialEndsAt: string | null = null;
      if (trialStartedAt && !trialConsumedAt) {
        const startMs = new Date(trialStartedAt).getTime();
        if (Number.isFinite(startMs)) {
          const endMs = startMs + TRIAL_DURATION_MS;
          if (endMs > Date.now()) {
            isOnTrial = true;
            trialEndsAt = new Date(endMs).toISOString();
          }
        }
      }

      // Provider state is derived BEFORE the free-access short-circuit so a
      // user with complimentary access who also holds a real Apple/Stripe
      // subscription still sees the paid state (and never gets offered a
      // second charge).
      const derived = deriveSubscriptionState(subRows);
      const activePaidRow =
        subRows.find(
          (r) =>
            (r.purchase_platform ?? 'stripe') !== null &&
            (!r.current_period_end || new Date(r.current_period_end).getTime() > Date.now()),
        ) ?? null;

      // Free access — absolute priority
      if (profileResult.data?.has_free_access === true) {
        console.log('✅ USER HAS FREE ACCESS — GRANTING PRO');
        const s: SubscriptionState = {
          ...DEFAULT_STATE,
          hasActiveSubscription: true,
          hasFreeAccess: true,
          needsPasswordSetup,
          isOnTrial,
          trialEndsAt,
          isInitiallyLoaded: true,
          lastCheckedAt: Date.now(),
          subscriptionProvider: derived.subscriptionProvider,
          hasActiveStripeSubscription: derived.hasActiveStripeSubscription,
          hasActiveAppleSubscription: derived.hasActiveAppleSubscription,
          hasActiveGooglePlaySubscription: derived.hasActiveGooglePlaySubscription,
          hasMultiplePaidSubscriptions: derived.hasMultiplePaidSubscriptions,
          subscriptionProductId: activePaidRow?.product_id ?? null,
          subscriptionEndsAt: activePaidRow?.current_period_end ?? null,
          billingManagementType:
            resolveBillingManagementType({
              hasFreeAccess: true,
              hasCohortAccess: false,
              isOnTrial,
              subscriptionProvider: derived.subscriptionProvider,
            }) ?? 'free',
        };
        setState(s);
        writeCache(s, user.id);
        return;
      }

      // Cohort access
      const hasCohortAccess = hasCohortAccessFromEnrollment(activeEnrollment);
      if (hasCohortAccess) {
        console.log('🏫 [SubscriptionContext] COHORT ACCESS ACTIVE — granting PRO', {
          cohortId: activeEnrollment?.cohort_id,
          startAt: activeEnrollment?.start_at,
          endAt: activeEnrollment?.end_at,
        });
      }

      // Regular subscription — derive provider-neutral state from ALL active
      // rows. Preserves existing entitlement logic: any active/unexpired row
      // still grants access; provider fields are additive/informational.
      const hasActiveSub = derived.hasActiveSubscription;

      // Active in-app trial grants PRO access just like a paid sub.
      const hasAccess = hasActiveSub || hasCohortAccess || isOnTrial;
      console.log('💳 [SubscriptionContext] Final access:', {
        hasActiveSub,
        hasCohortAccess,
        isOnTrial,
        trialEndsAt,
        hasAccess,
        provider: derived.subscriptionProvider,
      });

      const billingManagementType = resolveBillingManagementType({
        hasFreeAccess: false,
        hasCohortAccess,
        isOnTrial,
        subscriptionProvider: derived.subscriptionProvider,
      });

      const s: SubscriptionState = {
        hasActiveSubscription: hasAccess,
        hasCohortAccess,
        isBlocked: false,
        hasFreeAccess: false,
        needsPasswordSetup,
        isOnTrial,
        trialEndsAt,
        isInitiallyLoaded: true,
        lastCheckedAt: Date.now(),
        subscriptionProvider: derived.subscriptionProvider,
        hasActiveStripeSubscription: derived.hasActiveStripeSubscription,
        hasActiveAppleSubscription: derived.hasActiveAppleSubscription,
        hasActiveGooglePlaySubscription: derived.hasActiveGooglePlaySubscription,
        hasMultiplePaidSubscriptions: derived.hasMultiplePaidSubscriptions,
        subscriptionProductId: activePaidRow?.product_id ?? null,
        subscriptionEndsAt: activePaidRow?.current_period_end ?? null,
        billingManagementType,
      };
      setState(s);
      writeCache(s, user.id);

      // --- Stripe self-healing safety net -------------------------------
      // Only when the user ends up WITHOUT access. A lost Stripe webhook can
      // leave a paying customer with a stale local row; the edge function
      // decides server-side whether a Stripe lookup is actually justified
      // (healthy rows and Apple/Google rows never trigger a Stripe call) and
      // throttles repeats. Runs at most once per session per user.
      if (!hasAccess && selfHealAttemptedRef.current !== user.id) {
        selfHealAttemptedRef.current = user.id;
        // Pass a freshly-read access token explicitly: the invoke may otherwise
        // fire before the SDK has hydrated/refreshed the session, which makes
        // the edge function answer 401 (harmless, but noisy).
        supabase.auth
          .getSession()
          .then(({ data: { session } }) => {
            const token = session?.access_token;
            if (!token) {
              // No usable session yet — allow a retry on the next check.
              selfHealAttemptedRef.current = null;
              return { data: null, error: null } as { data: any; error: any };
            }
            return supabase.functions.invoke('reconcile-my-subscription', {
              headers: { Authorization: `Bearer ${token}` },
            });
          })
          .then(({ data, error }) => {
            if (error) {
              console.warn('🩹 [SubscriptionContext] self-heal failed:', error.message);
              return;
            }
            if (data?.changed) {
              console.log('🩹 [SubscriptionContext] Subscription reconciled from Stripe:', data.outcome);
              checkSubscriptionRef.current?.();
            }
          })
          .catch((e) => console.warn('🩹 [SubscriptionContext] self-heal threw:', e));
      }
    } catch (err) {
      console.error('Error checking subscription:', err);
      setState(prev => ({ ...prev, isInitiallyLoaded: true, lastCheckedAt: Date.now() }));
    }
    // Depend on user?.id (stable) instead of the full user object — Supabase
    // refreshes return a new user reference even when the id is unchanged,
    // which would otherwise recreate checkSubscription on every token refresh
    // and re-trigger every effect that depends on it (mount fetch, realtime
    // channel re-subscription, interval re-creation).
  }, [user?.id]);

  // Stable handle so the self-heal callback can re-run the check without
  // recreating checkSubscription (which would retrigger every dependent effect).
  const checkSubscriptionRef = useRef<(() => void) | null>(null);
  checkSubscriptionRef.current = checkSubscription;

  const revalidate = useCallback(async () => {
    await checkSubscription();
  }, [checkSubscription]);

  // --- Always revalidate on mount (never trust cache as final truth) ---
  useEffect(() => {
    if (user) checkSubscription();
  }, [user?.id, checkSubscription]);

  // --- Background revalidation — deferred during active exams ---
  const { examInProgress } = useExamSession();

  // --- Revalidate when app returns to foreground (throttled 30s, skipped during exam) ---
  const lastForegroundCheckRef = useRef<number>(0);
  const FOREGROUND_THROTTLE_MS = 30_000;
  useEffect(() => {
    if (!user?.id) return;
    let cancelIdle: (() => void) | undefined;
    const unsubscribe = onAppResume(() => {
      if (examInProgress) {
        console.log('🛡️ [SubscriptionContext] Exam in progress — skipping foreground revalidation');
        return;
      }
      const now = Date.now();
      if (now - lastForegroundCheckRef.current < FOREGROUND_THROTTLE_MS) {
        console.log('⏱️ [SubscriptionContext] Foreground revalidation throttled');
        return;
      }
      lastForegroundCheckRef.current = now;
      // Deferred to idle: the revalidation network + state work used to run
      // synchronously on the resume frame, delaying interactivity ~2s on iOS.
      cancelIdle = runWhenIdle(() => {
        console.log('👁️ [SubscriptionContext] App foregrounded — revalidating access (idle)');
        checkSubscription();
      }, 1500);
    });
    return () => {
      unsubscribe();
      cancelIdle?.();
    };
  }, [user?.id, checkSubscription, examInProgress]);


  // Background interval — reads lastCheckedAt from a ref so updating it does
  // NOT recreate the interval (previously every check tore down and rebuilt
  // the timer, causing a subtle revalidation cascade under load).
  const lastCheckedAtRef = useRef<number>(state.lastCheckedAt || 0);
  useEffect(() => {
    lastCheckedAtRef.current = state.lastCheckedAt || 0;
  }, [state.lastCheckedAt]);
  useEffect(() => {
    if (!user?.id || !state.isInitiallyLoaded || examInProgress) return;
    const interval = setInterval(() => {
      const timeSinceLastCheck = Date.now() - lastCheckedAtRef.current;
      if (timeSinceLastCheck > REVALIDATE_INTERVAL) {
        console.log('🔄 [SubscriptionContext] Background revalidation');
        checkSubscription();
      }
    }, REVALIDATE_INTERVAL);
    return () => clearInterval(interval);
  }, [user?.id, state.isInitiallyLoaded, checkSubscription, examInProgress]);

  // --- Real-time: invalidate cache immediately on DB changes ---
  useEffect(() => {
    if (!user?.id) return;

    const invalidateAndRecheck = () => {
      clearSubscriptionCache();
      blockedCacheRef.current = null;
      // Drop shared enrollment cache so the next fetch hits Postgres
      queryClient.invalidateQueries({ queryKey: activeEnrollmentQueryKey(user.id) });
      checkSubscription();
    };

    const handleChannelError = (channelName: string) => (status: string, err?: Error) => {
      if (status === 'SUBSCRIBED') {
        console.log(`📡 [SubRT:${channelName}] Subscribed`);
        return;
      }
      if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        console.warn(`📡 [SubRT:${channelName}] ${status} — fallback refetch`, err?.message);
        invalidateAndRecheck();
        return;
      }
      if (status === 'CLOSED') {
        console.log(`📡 [SubRT:${channelName}] Closed`);
      }
    };

    // Consolidated single channel for all subscription-related tables
    const accessChannel = supabase
      .channel(`access-changes-${user.id}`)
      .on('postgres_changes' as any, { event: '*', schema: 'public', table: 'subscriptions', filter: `user_id=eq.${user.id}` },
        () => { console.log('📢 [SubRT] subscription changed'); invalidateAndRecheck(); })
      .on('postgres_changes' as any, { event: '*', schema: 'public', table: 'blocked_users', filter: `user_id=eq.${user.id}` },
        () => { console.log('📢 [SubRT] blocked_users changed'); invalidateAndRecheck(); })
      .on('postgres_changes' as any, { event: 'UPDATE', schema: 'public', table: 'profiles', filter: `user_id=eq.${user.id}` },
        () => { console.log('📢 [SubRT] profiles changed'); setTimeout(() => invalidateAndRecheck(), 100); })
      .on('postgres_changes' as any, { event: '*', schema: 'public', table: 'institutional_student_cohorts', filter: `user_id=eq.${user.id}` },
        () => { console.log('📢 [SubRT] cohort enrollment changed'); invalidateAndRecheck(); })
      .subscribe(handleChannelError('access'));

    return () => {
      supabase.removeChannel(accessChannel);
    };
  }, [user?.id, checkSubscription]);

  // Memoize the context value so consumers do not re-render on every parent
  // render — only when an actual state field or `revalidate` identity changes.
  const contextValue = useMemo(
    () => ({
      hasActiveSubscription: state.hasActiveSubscription,
      hasCohortAccess: state.hasCohortAccess,
      isBlocked: state.isBlocked,
      hasFreeAccess: state.hasFreeAccess,
      needsPasswordSetup: state.needsPasswordSetup,
      isOnTrial: state.isOnTrial,
      trialEndsAt: state.trialEndsAt,
      isInitiallyLoaded: state.isInitiallyLoaded,
      lastCheckedAt: state.lastCheckedAt,
      subscriptionProvider: state.subscriptionProvider,
      hasActiveStripeSubscription: state.hasActiveStripeSubscription,
      hasActiveAppleSubscription: state.hasActiveAppleSubscription,
      hasActiveGooglePlaySubscription: state.hasActiveGooglePlaySubscription,
      hasMultiplePaidSubscriptions: state.hasMultiplePaidSubscriptions,
      subscriptionProductId: state.subscriptionProductId,
      subscriptionEndsAt: state.subscriptionEndsAt,
      billingManagementType: state.billingManagementType,
      revalidate,
    }),
    [
      state.hasActiveSubscription,
      state.hasCohortAccess,
      state.isBlocked,
      state.hasFreeAccess,
      state.needsPasswordSetup,
      state.isOnTrial,
      state.trialEndsAt,
      state.isInitiallyLoaded,
      state.lastCheckedAt,
      state.subscriptionProvider,
      state.hasActiveStripeSubscription,
      state.hasActiveAppleSubscription,
      state.hasActiveGooglePlaySubscription,
      state.hasMultiplePaidSubscriptions,
      state.subscriptionProductId,
      state.subscriptionEndsAt,
      state.billingManagementType,
      revalidate,
    ],
  );

  return (
    <SubscriptionContext.Provider value={contextValue}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscriptionContext = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error("useSubscriptionContext must be used within SubscriptionProvider");
  }
  return context;
};
